import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { interval, Subscription } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';
import { formatDate } from '../../utils/date-format.util';

/** Show a soft reminder this long before password expiry (default 24h). */
const WARNING_BEFORE_MS = 24 * 60 * 60 * 1000;
const DISMISS_SESSION_KEY = 'password_expiry_banner_dismissed_for';

@Component({
  selector: 'app-password-expiry-banner',
  standalone: true,
  imports: [CommonModule, RouterModule],
  template: `
    @if (visible) {
      <div
        class="mb-4 flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 shadow-sm dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-100 sm:flex-row sm:items-center sm:justify-between"
        role="status"
      >
        <div class="flex items-start gap-2 sm:items-center">
          <i class="bi bi-exclamation-triangle mt-0.5 shrink-0 text-lg text-amber-600 dark:text-amber-400 sm:mt-0"></i>
          <p class="leading-relaxed">{{ message }}</p>
        </div>
        <div class="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
          <a
            routerLink="/profile"
            class="inline-flex items-center justify-center rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-amber-700 dark:bg-amber-600 dark:hover:bg-amber-500"
          >
            Change password
          </a>
          <button
            type="button"
            (click)="dismiss()"
            class="rounded-lg px-3 py-1.5 text-xs font-medium text-amber-800 underline-offset-2 hover:underline dark:text-amber-200"
          >
            Dismiss
          </button>
        </div>
      </div>
    }
  `,
})
export class PasswordExpiryBannerComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);

  visible = false;
  message = '';

  private sub?: Subscription;
  private expiryMsForBanner: number | null = null;

  ngOnInit(): void {
    this.sub = interval(60_000)
      .pipe(startWith(0))
      .subscribe(() => this.refresh());
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  dismiss(): void {
    if (this.expiryMsForBanner != null) {
      sessionStorage.setItem(DISMISS_SESSION_KEY, String(this.expiryMsForBanner));
    }
    this.visible = false;
  }

  private refresh(): void {
    if (this.authService.isPasswordResetRequired()) {
      this.visible = false;
      return;
    }

    const exp = this.authService.getPasswordExpiresAtMs();
    if (exp == null) {
      this.visible = false;
      return;
    }

    const now = Date.now();
    if (now >= exp) {
      this.visible = false;
      return;
    }

    const remaining = exp - now;
    if (remaining > WARNING_BEFORE_MS) {
      this.visible = false;
      return;
    }

    const dismissedFor = sessionStorage.getItem(DISMISS_SESSION_KEY);
    if (dismissedFor === String(exp)) {
      this.visible = false;
      return;
    }

    this.expiryMsForBanner = exp;
    this.message = this.buildMessage(exp, remaining);
    this.visible = true;
  }

  private buildMessage(expMs: number, remainingMs: number): string {
    const hours = Math.max(1, Math.ceil(remainingMs / (60 * 60 * 1000)));
    const dateStr = formatDate(expMs);
    return `Your password expires in about ${hours} hour${hours === 1 ? '' : 's'} (by ${dateStr}). Change it now to avoid losing access when it expires.`;
  }
}
